/**
 * Composant d'affichage des informations de suivi pour une commande avec livraison
 * Affiche le numéro de tracking, transporteur, dates, statut estimé
 * Design horizontal moderne
 * Créé le 21 juillet 2026
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import './DeliveryTrackingDisplay.css';

const DeliveryTrackingDisplay = ({ reservationId, reservation, isOwner = false }) => {
  const { t } = useTranslation('delivery');
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 🆕 Priorité 1: Vérifier si owner_notes contient les données de tracking (format JSON)
    if (reservation?.owner_notes && reservation.pickup_confirmed) {
      try {
        const ownerNotesData = typeof reservation.owner_notes === 'string' 
          ? JSON.parse(reservation.owner_notes) 
          : reservation.owner_notes;

        if (ownerNotesData && ownerNotesData.tracking_number) {
          // Utiliser l'URL personnalisée si présente, sinon générer l'URL automatique
          const trackingUrl = ownerNotesData.custom_tracking_url 
            || getCarrierTrackingUrl(ownerNotesData.carrier, ownerNotesData.tracking_number);
          
          setTrackingInfo({
            tracking_number: ownerNotesData.tracking_number,
            carrier: ownerNotesData.carrier,
            shipped_at: ownerNotesData.shipped_at,
            estimated_delivery: ownerNotesData.estimated_delivery,
            custom_tracking_url: ownerNotesData.custom_tracking_url,
            notes: ownerNotesData.notes,
            tracking_url: trackingUrl,
            is_shipped: true,
            is_completed: reservation.return_confirmed || false
          });
          setLoading(false);
          return;
        }
      } catch (e) {
        console.error('Error parsing owner_notes for tracking:', e);
      }
    }

    // Priorité 2: Appel API si pas de données dans owner_notes
    fetchTrackingInfo();
  }, [reservationId, reservation]);

  const getCarrierTrackingUrl = (carrier, trackingNumber) => {
    const carrierUrls = {
      'colissimo': `https://www.laposte.fr/outils/suivre-vos-envois?code=${trackingNumber}`,
      'chronopost': `https://www.chronopost.fr/tracking-no-cms/suivi-page?listeNumerosLT=${trackingNumber}`,
      'dhl': `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`,
      'ups': `https://www.ups.com/track?tracknum=${trackingNumber}`,
      'fedex': `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
    };
    const carrierKey = carrier?.toLowerCase();
    return carrierUrls[carrierKey] || `https://www.google.com/search?q=${encodeURIComponent(carrier + ' ' + trackingNumber)}`;
  };

  const fetchTrackingInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(
        `/delivery-orders/${reservationId}/tracking`
      );

      if (response.data.success) {
        setTrackingInfo(response.data.tracking_info);
      }

    } catch (err) {
      console.error('Error fetching tracking info:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des informations de suivi');
    } finally {
      setLoading(false);
    }
  };

  const getCarrierLogo = (carrier) => {
    const carriers = {
      'colissimo': '📮',
      'chronopost': '⚡',
      'dhl': '📦',
      'ups': '🚚',
      'fedex': '✈️',
      'other': '📦'
    };
    return carriers[carrier?.toLowerCase()] || '📦';
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.notSpecified', 'Non spécifiée');
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // 🆕 Calculer le statut de livraison estimé
  const getDeliveryStatus = () => {
    if (!trackingInfo || !trackingInfo.estimated_delivery) {
      return { status: 'in_transit', isPastEstimate: false };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const estimatedDate = new Date(trackingInfo.estimated_delivery);
    estimatedDate.setHours(0, 0, 0, 0);

    const isPastEstimate = today >= estimatedDate;
    
    return {
      status: isPastEstimate ? 'delivered_estimated' : 'in_transit',
      isPastEstimate,
      daysLate: isPastEstimate ? Math.floor((today - estimatedDate) / (1000 * 60 * 60 * 24)) : 0
    };
  };

  if (loading) {
    return (
      <div className="delivery-tracking-display loading">
        <div className="spinner-large"></div>
        <p>{t('loadingTracking', 'Chargement des informations de suivi...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="delivery-tracking-display error">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  if (!trackingInfo || !trackingInfo.tracking_number) {
    return (
      <div className="delivery-tracking-display no-tracking">
        <p className="icon">📦</p>
        <p className="message">
          {isOwner 
            ? t('noTrackingOwner', 'Aucune information de suivi. Veuillez confirmer l\'expédition.')
            : t('noTrackingRenter', 'Le vendeur n\'a pas encore expédié votre commande.')
          }
        </p>
      </div>
    );
  }

  const deliveryStatus = getDeliveryStatus();

  return (
    <div className="delivery-tracking-display modern">
      {/* En-tête avec statut */}
      <div className="tracking-header-modern">
        <div className="header-left">
          <h3 className="tracking-title-modern">
            {getCarrierLogo(trackingInfo.carrier)} {t('trackingInfo', 'Informations de suivi')}
          </h3>
          <p className="tracking-subtitle">{trackingInfo.carrier}</p>
        </div>
        <div className="header-right">
          {deliveryStatus.status === 'delivered_estimated' ? (
            <div className="status-badge-large estimated-delivered">
              <span className="status-icon">📦✓</span>
              <div className="status-text">
                <strong>{t('deliveredEstimated', 'Livré (estimé)')}</strong>
                <small>{t('basedOnEstimate', 'Selon date estimée')}</small>
              </div>
            </div>
          ) : (
            <div className="status-badge-large in-transit">
              <span className="status-icon">🚚</span>
              <div className="status-text">
                <strong>{t('inTransit', 'En cours de livraison')}</strong>
                <small>{t('estimatedArrival', 'Arrivée') + ' : ' + formatDate(trackingInfo.estimated_delivery)}</small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Message d'information si livraison estimée */}
      {deliveryStatus.status === 'delivered_estimated' && !isOwner && (
        <div className="estimated-delivery-notice">
          <div className="notice-icon">💡</div>
          <div className="notice-content">
            <p className="notice-title">{t('estimatedDeliveryNoticeTitle', 'Livraison estimée atteinte')}</p>
            <p className="notice-text">
              {t('estimatedDeliveryNoticeText', 
                'La date de livraison estimée est atteinte ou dépassée. Nous n\'avons pas de confirmation de réception du transporteur. Si vous n\'avez rien reçu :'
              )}
            </p>
            <ol className="notice-steps">
              <li>{t('contactSellerFirst', '1. Contactez d\'abord le vendeur pour vérifier l\'état de la livraison')}</li>
              <li>{t('checkWithNeighbors', '2. Vérifiez auprès de vos voisins ou point relais si applicable')}</li>
              <li>{t('contactSupportIfNeeded', '3. Si aucune solution, contactez notre support avec votre numéro de suivi')}</li>
            </ol>
          </div>
        </div>
      )}

      {/* Informations de tracking en layout horizontal */}
      <div className="tracking-grid">
        <div className="tracking-card">
          <div className="card-icon">🔢</div>
          <div className="card-content">
            <span className="card-label">{t('trackingNumber', 'Numéro de suivi')}</span>
            <span className="card-value tracking-number">
              {trackingInfo.tracking_number}
              <button
                onClick={() => navigator.clipboard.writeText(trackingInfo.tracking_number)}
                className="copy-btn-inline"
                title={t('common.copy', 'Copier')}
              >
                📋
              </button>
            </span>
          </div>
        </div>

        <div className="tracking-card">
          <div className="card-icon">📅</div>
          <div className="card-content">
            <span className="card-label">{t('shippedAt', 'Expédié le')}</span>
            <span className="card-value">{formatDate(trackingInfo.shipped_at)}</span>
          </div>
        </div>

        <div className="tracking-card">
          <div className="card-icon">🎯</div>
          <div className="card-content">
            <span className="card-label">{t('estimatedDelivery', 'Livraison estimée')}</span>
            <span className="card-value">{formatDate(trackingInfo.estimated_delivery)}</span>
          </div>
        </div>

        {trackingInfo.custom_tracking_url && (
          <div className="tracking-card">
            <div className="card-icon">🔗</div>
            <div className="card-content">
              <span className="card-label">{t('customTrackingUrl', 'Suivi vendeur')}</span>
              <a 
                href={trackingInfo.custom_tracking_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="card-link"
              >
                {t('openCustomTracking', 'Ouvrir')} →
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Bouton de tracking principal */}
      {trackingInfo.tracking_url && (
        <div className="tracking-action-modern">
          <a
            href={trackingInfo.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-track-modern"
          >
            <span className="btn-icon">🔍</span>
            <span className="btn-text">{t('trackPackage', 'Suivre mon colis sur ' + trackingInfo.carrier)}</span>
            <span className="btn-arrow">→</span>
          </a>
        </div>
      )}

      {/* Timeline horizontale */}
      <div className="timeline-horizontal">
        <div className="timeline-step completed">
          <div className="step-icon">✓</div>
          <div className="step-line"></div>
          <p className="step-label">{t('orderConfirmed', 'Commande confirmée')}</p>
          <p className="step-date">{formatDate(trackingInfo.shipped_at)}</p>
        </div>

        <div className="timeline-step completed">
          <div className="step-icon">✓</div>
          <div className="step-line"></div>
          <p className="step-label">{t('packageShipped', 'Colis expédié')}</p>
          <p className="step-date">{formatDate(trackingInfo.shipped_at)}</p>
        </div>

        <div className={`timeline-step ${deliveryStatus.status === 'delivered_estimated' ? 'completed' : 'active'}`}>
          <div className="step-icon">{deliveryStatus.status === 'delivered_estimated' ? '✓' : '🚚'}</div>
          <div className="step-line"></div>
          <p className="step-label">{t('inTransit', 'En transit')}</p>
          <p className="step-date">{deliveryStatus.status === 'delivered_estimated' ? t('now', 'Maintenant') : '...'}</p>
        </div>

        <div className={`timeline-step ${deliveryStatus.status === 'delivered_estimated' ? 'active' : 'pending'}`}>
          <div className="step-icon">
            {deliveryStatus.status === 'delivered_estimated' ? '📦' : '⏳'}
          </div>
          <p className="step-label">
            {deliveryStatus.status === 'delivered_estimated' 
              ? t('deliveredEstimated', 'Livré (estimé)')
              : t('delivery', 'Livraison')}
          </p>
          <p className="step-date">{formatDate(trackingInfo.estimated_delivery)}</p>
        </div>
      </div>

      {/* Notes si présentes */}
      {trackingInfo.notes && (
        <div className="tracking-notes">
          <strong>{t('notes', 'Notes')} :</strong> {trackingInfo.notes}
        </div>
      )}

      {/* Info box client */}
      {!isOwner && (
        <div className="info-box-client-modern">
          <span className="info-icon">ℹ️</span>
          <p>
            {t('clientInfo', 
              'Vous disposez de 21 jours à compter de la date d\'expédition pour signaler tout problème concernant votre commande. Contactez le support si vous rencontrez un problème.'
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default DeliveryTrackingDisplay;
